import { useEffect, useState } from 'react'
import { BILLING_PLANS_PATH, mapPlanCodeToBillingPlanId, parseBillingPlansCatalog, type BillingPlansCatalog } from '@/api/billingApi'
import { LANDING_PRICING } from './pricingConfig'

/**
 * Цены и лимиты тарифов для лендинга — из того же GET /api/billing/plans, что и страница биллинга,
 * чтобы правка в базе сразу отражалась на сайте. Пока ответ не пришёл (или API недоступен),
 * показываем LANDING_PRICING как запасной вариант.
 */
export type LandingPlanId = 'start' | 'pro' | 'max'
export type LandingPeriod = { m: number; off: number }
export type LandingPlanLimits = {
  channels: number | null
  regionsPerChannel: number | null
  commonTemplateSections: number | null
  personalTemplateSections: number | null
  removeBranding: boolean | null
  customizationLevel: string | null
}
export type LandingPricing = {
  plans: Record<LandingPlanId, number>
  periods: LandingPeriod[]
  limits: Partial<Record<LandingPlanId, LandingPlanLimits>>
  popular: LandingPlanId
  fromServer: boolean
}

export const FALLBACK_PRICING: LandingPricing = {
  plans: { ...LANDING_PRICING.plans },
  periods: LANDING_PRICING.periods.map((p) => ({ m: p.m, off: p.off })),
  limits: {},
  popular: 'pro',
  fromServer: false,
}

let cached: LandingPricing | null = null
let inFlight: Promise<LandingPricing | null> | null = null
const listeners = new Set<() => void>()

function catalogToPricing(catalog: BillingPlansCatalog): LandingPricing | null {
  // Лендинг считает в долларах; если бэк отдаёт другую валюту, запасной вариант честнее, чем рубли под знаком $.
  if ((catalog.currency || 'USD').toUpperCase() !== 'USD') return null
  const plans: Record<LandingPlanId, number> = { ...FALLBACK_PRICING.plans }
  const limits: Partial<Record<LandingPlanId, LandingPlanLimits>> = {}
  let popular: LandingPlanId | null = null
  let found = 0
  for (const plan of catalog.plans) {
    if (plan.kind.toUpperCase() === 'TRIAL') continue
    const id = mapPlanCodeToBillingPlanId(plan.code)
    if (!id || !(plan.unitPriceCents > 0)) continue
    plans[id] = plan.unitPriceCents / 100
    found++
    if (plan.popular) popular = id
    if (plan.hasLimits) {
      limits[id] = {
        channels: plan.limits.channelLimit,
        regionsPerChannel: plan.limits.regionLimit,
        commonTemplateSections: plan.limits.commonTemplateSectionLimit,
        personalTemplateSections: plan.limits.personalTemplateSectionLimit,
        removeBranding: plan.limits.canRemoveBranding,
        customizationLevel: plan.limits.customizationLevel || null,
      }
    }
  }
  if (found === 0) return null
  const source = catalog.periods.length > 0 ? catalog.periods : catalog.plans.flatMap((p) => p.periods)
  const byMonths = new Map<number, number>()
  for (const period of source) {
    if (period.periodMonths > 0 && !byMonths.has(period.periodMonths)) {
      byMonths.set(period.periodMonths, Math.max(0, Math.round(period.discountBps / 100)))
    }
  }
  const periods = [...byMonths.entries()].sort((a, b) => a[0] - b[0]).map(([m, off]) => ({ m, off }))
  return {
    plans,
    periods: periods.length > 0 ? periods : FALLBACK_PRICING.periods,
    limits,
    popular: popular ?? 'pro',
    fromServer: true,
  }
}

export async function loadLandingPricing(apiBaseUrl: string, authHeaders: Record<string, string> = {}): Promise<LandingPricing | null> {
  if (cached) return cached
  if (inFlight) return inFlight
  inFlight = (async () => {
    try {
      const base = (apiBaseUrl || '').replace(/\/$/, '')
      if (!base) return null
      // Обычный fetch, не billingFetch: на лендинге гость, и 401 здесь не должен вести на логин.
      const res = await fetch(`${base}${BILLING_PLANS_PATH}`, { headers: { Accept: 'application/json', ...authHeaders } })
      if (!res.ok) return null
      const pricing = catalogToPricing(parseBillingPlansCatalog(await res.json()))
      if (pricing) {
        cached = pricing
        listeners.forEach((fn) => fn())
      }
      return pricing
    } catch {
      return null
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

export function useLandingPricing(apiBaseUrl: string, authHeaders?: Record<string, string>): LandingPricing {
  const [pricing, setPricing] = useState<LandingPricing>(() => cached ?? FALLBACK_PRICING)
  useEffect(() => {
    const listener = () => {
      if (cached) setPricing(cached)
    }
    listeners.add(listener)
    void loadLandingPricing(apiBaseUrl, authHeaders)
    return () => {
      listeners.delete(listener)
    }
    // authHeaders — только для залогиненных, на результат (публичные цены) не влияют: не перезапрашиваем при смене токена.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl])
  return pricing
}

/** «До 3» / «без ограничений» для строки лимита; null — лимит бэком не передан, остаётся текст по умолчанию. */
export function limitLabel(value: number | null | undefined, unlimitedText: string, upTo: (n: number) => string): string | null {
  if (value === undefined) return null
  if (value === null) return unlimitedText
  return upTo(value)
}
